import { useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { useStoreActions, useStoreState } from '../models/RootStore';
import { getSiteBacnetUnits } from './useBacnetDevicesApi';
import { getSiteModbusUnitsWithDevices } from './useModbusDevicesApi';
import { useGetSiteBacnetParamTemplates } from './useBacnetParamTemplatesApi';
import { useGetSiteBacnetUiTemplates } from './useBacnetUiTemplatesApi';
import { useIsModbusEnabled } from '../screens/Modbus/modbusFeature';
import { toUnitShape } from '../utils/universalUnit/toUnitShape';
import {
  getGroupUniversalUnitIds,
  getUniversalEntryMappedPoints,
  resolveBacnetParamTemplate,
  UniversalGroupMember,
} from '../utils/universalUnit/groups';

interface Options {
  group: any;
  siteId?: string;
  /** Track live values over the websocket and synthesize unit shapes for the group status */
  live?: boolean;
}

// Control screens hand in groups whose bacnetUnits/modbusUnits are already id -> entry maps,
// the API group only has ids, so those entries are fetched from the site
const getProvidedEntries = (members: any) =>
  members && !Array.isArray(members) && typeof members === 'object' ? members : null;

export const useGroupUniversalMembers = ({ group, siteId: siteIdProp, live = false }: Options) => {
  const { enableBacnet } = useStoreState((s) => s.config);
  const enableModbus = useIsModbusEnabled();
  const types = useStoreState((s) => s.types);
  const operationModesMirror = useStoreState((s) => s.operationModesMirror);
  const operationStatusesMirror = useStoreState((s) => s.operationStatusesMirror);
  const fanModesMirror = useStoreState((s) => s.fanModesMirror);
  const swingModesMirror = useStoreState((s) => s.swingModesMirror);
  const temperatureScaleMirror = useStoreState((s) => s.temperatureScaleMirror);
  const { temperatureScale } = useStoreState((s) => s.users.me);
  const { addWebSocketListener, removeWebSocketListener } = useStoreActions((a: any) => a);

  const siteId = siteIdProp || group?.site;
  const { bacnetUnits: bacnetIds, modbusUnits: modbusIds } = getGroupUniversalUnitIds(group);
  const providedBacnet = getProvidedEntries(group?.bacnetUnits);
  const providedModbus = getProvidedEntries(group?.modbusUnits);
  const needsBacnet = !!enableBacnet && bacnetIds.length > 0;
  const needsModbus = !!enableModbus && modbusIds.length > 0;
  const fetchBacnet = needsBacnet && !providedBacnet;
  const fetchModbus = needsModbus && !providedModbus;

  const { data: siteUiTemplates } = useGetSiteBacnetUiTemplates((needsBacnet ? siteId : undefined) as string) as any;
  const { data: siteParamTemplates } = useGetSiteBacnetParamTemplates((needsBacnet ? siteId : undefined) as string) as any;

  const [fetchedBacnet, setFetchedBacnet] = useState<Record<string, any>>({});
  const [fetchedModbus, setFetchedModbus] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!siteId || (!fetchBacnet && !fetchModbus)) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    Promise.all([
      fetchBacnet ? getSiteBacnetUnits(siteId) : {},
      fetchModbus ? getSiteModbusUnitsWithDevices(siteId) : {},
    ])
      .then(([bacnet, modbus]: any) => {
        if (cancelled) {
          return;
        }
        setFetchedBacnet(bacnet || {});
        setFetchedModbus(modbus || {});
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, fetchBacnet, fetchModbus]);

  const bacnetIdsKey = bacnetIds.join(',');
  const modbusIdsKey = modbusIds.join(',');

  const members: UniversalGroupMember[] = useMemo(() => {
    const templates = { siteUiTemplates: siteUiTemplates || {}, siteParamTemplates: siteParamTemplates || {} };
    const bacnetEntries = providedBacnet || fetchedBacnet;
    const modbusEntries = providedModbus || fetchedModbus;
    const result: UniversalGroupMember[] = [];
    if (needsBacnet) {
      bacnetIds.forEach((id) => {
        const entry = bacnetEntries[id];
        if (entry) {
          result.push({
            id,
            name: entry.name || entry.title || '',
            protocol: 'bacnet',
            entry,
            mappedPoints: getUniversalEntryMappedPoints(entry, 'bacnet', templates),
          });
        }
      });
    }
    if (needsModbus) {
      modbusIds.forEach((id) => {
        const entry = modbusEntries[id];
        if (entry) {
          result.push({
            id,
            name: entry.name || entry.title || '',
            protocol: 'modbus',
            entry,
            mappedPoints: getUniversalEntryMappedPoints(entry, 'modbus'),
          });
        }
      });
    }
    return result;
  }, [bacnetIdsKey, modbusIdsKey, providedBacnet, providedModbus, fetchedBacnet, fetchedModbus, siteUiTemplates, siteParamTemplates, needsBacnet, needsModbus]);

  // Live operational data per member, fed by the same websocket messages the unit rows listen to
  const [liveData, setLiveData] = useState<Record<string, Record<string, any>>>({});

  useEffect(() => {
    setLiveData({});
    if (!live || !members.length) {
      return;
    }
    const memberIds = new Set(members.map(({ id }) => String(id)));
    const handleMessage = (message: any) => {
      if (message.name !== 'UPDATE_BACNET_UNIT_DATA' && message.name !== 'UPDATE_MODBUS_UNIT_DATA') {
        return;
      }
      const unitId = String(message.data?.unitId);
      const operationalData = message.data?.operationalData;
      if (!memberIds.has(unitId) || isEmpty(operationalData)) {
        return;
      }
      setLiveData((prev) => ({ ...prev, [unitId]: { ...(prev[unitId] || {}), ...operationalData } }));
    };
    addWebSocketListener(handleMessage);
    return () => removeWebSocketListener(handleMessage);
  }, [live, members]);

  const unitShapes: Record<string, any> = useMemo(() => {
    if (!live) {
      return {};
    }
    const isCelsius = +(temperatureScaleMirror as any)?.celsius === +(temperatureScale as any);
    const typesStore = {
      types,
      operationModesMirror,
      operationStatusesMirror,
      fanModesMirror,
      swingModesMirror,
      operationModesExtended: types?.operationModesExtended,
    };
    const templates = { siteUiTemplates: siteUiTemplates || {}, siteParamTemplates: siteParamTemplates || {} };
    const result: Record<string, any> = {};
    members.forEach(({ id, entry, protocol }) => {
      result[id] = toUnitShape({
        entry,
        paramTemplate: protocol === 'bacnet' ? resolveBacnetParamTemplate(entry, templates) : undefined,
        rows: protocol === 'modbus' ? entry.rows : undefined,
        operationalData: { ...(entry.operationalData || {}), ...(liveData[id] || {}) },
        typesStore,
        isCelsius,
        protocol,
      });
    });
    return result;
  }, [live, members, liveData, types, operationModesMirror, operationStatusesMirror, fanModesMirror, swingModesMirror, temperatureScaleMirror, temperatureScale, siteUiTemplates, siteParamTemplates]);

  return { members, unitShapes, isLoading };
};
